import { useEffect, useState } from 'react';
import { filterSupportedEventListenerOptions, throttle } from '../../utils/index.js';

/**
 * Toggle sticky behaviour when the eyebrow and main nav bar meet.
 */
export const useStickyToggle = ({
  isDesktop,
  fixedPositioning,
  headerRef,
  eyebrowRef,
  navRef
}) => {
  const [isSticky, setSticky] = useState(false);

  /**
     * Handle the scoll event when using fixed positioning.
     *
     * Here we toggle the sticky behaviour based on scroll position and the
     * height of header elements minus the non-sticky branded section.
     *
     * On mobile and tablet the header is always sticky.
     */
  const fixedOnScroll = () => {
    if (!isDesktop) {
      setSticky(true);
      return;
    }
    const {
      height: headerHeight,
      y: headerYPos
    } = headerRef.current.getBoundingClientRect();
    const {
      height: eyebrowHeight
    } = eyebrowRef.current.getBoundingClientRect();
    const {
      height: mainNavHeight
    } = navRef.current.getBoundingClientRect();
    const heightToFix = isSticky ? headerHeight : headerHeight - eyebrowHeight - mainNavHeight;

    // In case the header isn't at the top of the screen (e.g. takeover ads)
    const yDiff = window.scrollY + headerYPos;
    setSticky(window.scrollY - yDiff > heightToFix);
  };
  /**
   * Handle the scroll event when not using fixed positioning.
   *
   * Here we toggle based on the position of the eyebrow and main nav. While
   * we use pure CSS for most of the sticky behaviour in this mode, we still
   * need a little JS to toggle the switch of the logo when they eyebrow joins
   * the main navigation bar.
   */
  const notFixedOnScroll = () => {
    const {
      y: navYPos
    } = navRef.current.getBoundingClientRect();
    const {
      height: eyebrowHeight,
      top: eyebrowTop
    } = eyebrowRef.current.getBoundingClientRect();
    setSticky(navYPos - eyebrowTop <= eyebrowHeight);
  };
  useEffect(() => {
    const evtOpts = filterSupportedEventListenerOptions({
      passive: true
    });
    const onScroll = throttle(fixedPositioning ? fixedOnScroll : notFixedOnScroll, 100);
    onScroll(); // Fire on inital load in case already scrolled

    window.addEventListener('scroll', onScroll, evtOpts);
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [isSticky, isDesktop]);
  return isSticky;
};

/**
   * Toggle behaviour around the active popup.
   */
export const useActivePopup = ({
  isDesktop
}) => {
  const mainNavPopupKey = 'main-navigation';
  const accountNavPopupKey = 'account';
  const [activePopup, setActivePopup] = useState(null);

  // Prevent the page body from scrolling when the main mobile menu is open
  // Note that we target the body element directly, rather than use React Helmet
  // as this needs to work in environments where React Helmet is not available
  // (e.g. when served via the static component service).
  useEffect(() => {
    if (activePopup === mainNavPopupKey || activePopup === accountNavPopupKey) {
      document.body.classList.add('has-overlay');
    } else {
      document.body.classList.remove('has-overlay');
    }
  }, [activePopup]);

  // Clear any active popups on resize to desktop viewport
  useEffect(() => {
    const evtOpts = filterSupportedEventListenerOptions({
      passive: true
    });
    const clearActivePopups = () => {
      if (activePopup && isDesktop) {
        setActivePopup(null);
      }
    };
    window.addEventListener('resize', clearActivePopups, evtOpts);
    return () => {
      window.removeEventListener('resize', clearActivePopups);
    };
  }, [activePopup, isDesktop]);
  return [activePopup, setActivePopup];
};